import React from 'react';

import Button from '../styled/Button';
import { useTranslation } from 'react-i18next';
import { CollectionPropertyValue } from '../RealtimeCollectionProvider';
import { Tag } from 'react-feather';
import { InputDropdown, InputDropdownRenderItem } from '../popover/Dropdown';
import { CollectionProperty, CollectionPropertyOption } from '@saga/shared';
import classNames from 'classnames';
import { TaskLabelColors } from '../tasks/LabelSelect';
import ColorPickerDropdown from '../ColorPickerDropdown';
import { v4 as uuidv4 } from 'uuid';

function CollectionPropertySelect({
    property,
    onChangeValue,
    onChangeProperty,
    showEmptyState = false,
    multiple = false,
    disabled = false,
    limit,
}: {
    property: CollectionPropertyValue;
    onChangeValue: (value: string | undefined) => void;
    onChangeProperty: (property: Partial<Omit<CollectionProperty, 'id' | 'values'>>) => void;
    limit?: number;
    showEmptyState?: boolean;
    multiple?: boolean;
    disabled?: boolean;
}) {
    const parsedValue: CollectionPropertyOption[] = property.value ? JSON.parse(property.value) : [];
    const selectedIds = parsedValue.map((option) => option.id);
    const selectedOptions = property.options?.filter((option) => selectedIds.includes(option.id)) ?? [];

    const [isOptionsDropdownOpen, setIsOptionsDropdownOpen] = React.useState(false);
    const [colorPickerOptionId, setColorPickerOptionId] = React.useState<string | null>(null);

    const divRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const { t } = useTranslation();

    return (
        <div
            ref={divRef}
            className={classNames('hover-trigger max-w-400', {
                'flex truncate rounded': selectedOptions.length,
            })}
        >
            {!selectedOptions.length && showEmptyState && (
                <Button.DashedPill disabled={disabled} onClick={() => setIsOptionsDropdownOpen(true)} ref={buttonRef}>
                    {t('collections.properties.select_option')}
                </Button.DashedPill>
            )}

            {!!selectedOptions.length && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedOptions
                        .sort((a, b) => (limit == null ? a.name.localeCompare(b.name) : 0))
                        .slice(0, limit)
                        .map((option) => (
                            <PropertyOptionPill
                                key={option.id}
                                option={option}
                                onRemove={() =>
                                    onChangeValue(
                                        multiple
                                            ? JSON.stringify(parsedValue.filter((v) => v.id !== option.id))
                                            : undefined,
                                    )
                                }
                                disabled={disabled}
                                onClick={() => {
                                    setIsOptionsDropdownOpen(true);
                                    setColorPickerOptionId(null);
                                }}
                                extra={
                                    limit != null && selectedOptions.length > limit
                                        ? ` +${selectedOptions.length - limit}`
                                        : ''
                                }
                            />
                        ))}
                </div>
            )}

            {isOptionsDropdownOpen && (
                <PropertyOptionsDropdown
                    attachToRef={buttonRef.current ? buttonRef : divRef}
                    isOpen={isOptionsDropdownOpen}
                    onClose={() => setIsOptionsDropdownOpen(false)}
                    onSelectOption={(id) => {
                        const option = property.options?.find((option) => option.id === id);
                        if (option) {
                            onChangeValue(JSON.stringify([...(multiple ? parsedValue : []), option]));
                        }

                        if (!multiple) {
                            setIsOptionsDropdownOpen(false);
                        }
                    }}
                    options={property.options ?? []}
                    onCreateOption={(title) => {
                        const newOption = { id: uuidv4(), name: title, color: TaskLabelColors[0] };
                        onChangeProperty({
                            options: [...(property.options ?? []), newOption],
                        });

                        onChangeValue(JSON.stringify([...(multiple ? parsedValue : []), newOption]));

                        setIsOptionsDropdownOpen(false);
                        setColorPickerOptionId(newOption.id);
                    }}
                    selectedIds={selectedOptions.map((option) => option.id)}
                    onRemoveOption={(id) => {
                        onChangeProperty({
                            options: (property.options ?? []).filter((option) => option.id !== id),
                        });
                    }}
                    showSelectedOptions={multiple}
                />
            )}
            {!!colorPickerOptionId && (
                <ColorPickerDropdown
                    attachToRef={divRef.current ? divRef : buttonRef}
                    isOpen={!!colorPickerOptionId}
                    onClose={() => setColorPickerOptionId(null)}
                    onSelectColor={(color) => {
                        onChangeProperty({
                            options: (property.options ?? []).map((option) =>
                                option.id === colorPickerOptionId ? { ...option, color } : option,
                            ),
                        });

                        setColorPickerOptionId(null);
                    }}
                    availableColors={TaskLabelColors}
                    selectedColor={TaskLabelColors[0]}
                />
            )}
        </div>
    );
}

function PropertyOptionPill({
    option,
    onRemove,
    disabled,

    onClick,
    extra,
}: {
    option: CollectionPropertyOption;
    onRemove: () => void;
    disabled: boolean;

    onClick: () => void;
    extra?: string;
}) {
    return (
        <div className="inline-block max-w-full" key={option.id}>
            <Button.RemovableOption disabled={disabled} onRemove={onRemove}>
                <div
                    onClick={(e) => {
                        if (disabled) return;
                        e.stopPropagation();
                        onClick();
                    }}
                    className={classNames('cursor-pointer space-x-1.5 flex items-center')}
                >
                    <div className="ml-1 w-2 h-2 rounded-full" style={{ backgroundColor: option.color }} />
                    <span className="truncate max-w-20">{option.name}</span>
                    {extra && <span className="text-xs text-saga-gray-500">{extra}</span>}
                </div>
            </Button.RemovableOption>
        </div>
    );
}

function PropertyOptionsDropdown({
    attachToRef,
    isOpen,
    onClose,
    options,
    selectedIds,
    onSelectOption,
    onRemoveOption,
    onCreateOption,
    showSelectedOptions = true,
}: {
    attachToRef: React.RefObject<HTMLElement>;
    isOpen: boolean;
    onClose(): void;
    options: CollectionPropertyOption[];
    selectedIds: string[];
    onSelectOption(id: string): void;
    onRemoveOption(id: string): void;
    onCreateOption(title: string): void;
    showSelectedOptions?: boolean;
}) {
    const { t } = useTranslation();

    return (
        <InputDropdown<CollectionPropertyOption | { id: null }>
            addTopPadding
            attachToRef={attachToRef}
            isOpen={isOpen}
            onClose={onClose}
            dropdownProps={{ align: 'left' }}
            onSubmit={({ selectedItem, search }) => {
                if (selectedItem.id) {
                    onSelectOption(selectedItem.id);
                } else {
                    onCreateOption(search);
                }
            }}
            inputProps={{
                placeholder: t('collections.properties.select_or_add_option') as string,
                title: t('collections.properties.select_or_add_option') as string,
            }}
            renderItems={({ search }) => {
                const selectedOptions = selectedIds
                    .map((id) => options.find((option) => option.id === id))
                    .filter(Boolean) as CollectionPropertyOption[];
                const filteredOptions = options
                    .filter(
                        (option) =>
                            option.name.toLowerCase().includes(search.toLowerCase()) &&
                            !selectedIds.includes(option.id),
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((option): InputDropdownRenderItem<CollectionPropertyOption> => {
                        return {
                            render({ isSelected, setSelection, submit }) {
                                return (
                                    <div key={option.id}>
                                        <Button.PopOverButton
                                            selected={isSelected}
                                            onClick={submit}
                                            onMouseEnter={setSelection}
                                            hoverable
                                        >
                                            <div className="space-x-2 flex items-center max-w-52">
                                                <div
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: option.color }}
                                                />
                                                <span className="truncate">{option.name}</span>
                                            </div>
                                        </Button.PopOverButton>
                                    </div>
                                );
                            },
                            item: option,
                        };
                    });

                return [
                    {
                        render({ setSelection }) {
                            return showSelectedOptions && selectedOptions.length > 0 ? (
                                <div
                                    key={'selected-options'}
                                    className="flex flex-wrap gap-2 max-w-52 py-2"
                                    onMouseEnter={() => setSelection()}
                                >
                                    {selectedOptions.map((option) => (
                                        <PropertyOptionPill
                                            key={option.id}
                                            option={option}
                                            onRemove={() => onRemoveOption(option.id)}
                                            disabled={false}
                                            onClick={() => {}}
                                        />
                                    ))}
                                </div>
                            ) : null;
                        },
                        item: { id: null },
                    },

                    {
                        render({ isSelected, setSelection, submit }) {
                            return (
                                <div key={'search-option'}>
                                    {showSelectedOptions ? (
                                        <div className="flex items-center h-[26px] px-2 py-1 font-normal text-xs text-saga-gray-500">
                                            {filteredOptions.length
                                                ? t('collections.properties.select_option')
                                                : t('collections.properties.add_new_option')}
                                        </div>
                                    ) : null}
                                    {search?.trim().length &&
                                    !options.some((option) => option.name.toLowerCase() === search.toLowerCase()) ? (
                                        <Button.PopOverButton
                                            selected={isSelected}
                                            onClick={submit}
                                            onMouseEnter={setSelection}
                                            hoverable
                                        >
                                            <div className="space-x-2 flex items-center">
                                                <Tag size={16} />
                                                <span>{`${search} — `}</span>
                                                <span>{t('collections.properties.add_new_option')}</span>
                                            </div>
                                        </Button.PopOverButton>
                                    ) : null}
                                </div>
                            );
                        },
                        item: { id: null },
                    },
                    ...filteredOptions,
                ];
            }}
        />
    );
}

export default CollectionPropertySelect;
